import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Heart, Plus, X, AlertCircle } from 'lucide-react';
import { useElderCare } from '@/contexts/ElderCareContext';
import { LivingSituation } from '@/types';

// --- Helper Functions ---
const calculateAge = (dob: string): number => {
  const today = new Date();
  const birthDate = new Date(dob);
  let age = today.getFullYear() - birthDate.getFullYear();
  const m = today.getMonth() - birthDate.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  return age;
};

const getInitials = (firstName: string, lastName: string) => {
  return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
};

export default function ElderCare() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { careProfiles, isLoaded, callElderCare } = useElderCare();

  // Local State
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isJoinModalOpen, setIsJoinModalOpen] = useState(false);

  // Form States
  const [createForm, setCreateForm] = useState({
    firstName: '',
    lastName: '',
    dateOfBirth: '',
    livingSituation: 'independent' as LivingSituation,
  });
  
  const [joinCode, setJoinCode] = useState('');

  // Handlers
  const handleCreateSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await callElderCare('createCareProfile', createForm);
    setIsCreateModalOpen(false);
    setCreateForm({ firstName: '', lastName: '', dateOfBirth: '', livingSituation: 'independent' });
  };

  const handleJoinSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await callElderCare('acceptCareCircleInvite', { inviteCode: joinCode });
    setIsJoinModalOpen(false);
    setJoinCode('');
  };

  if (!isLoaded) {
    return (
      <div className="flex h-full items-center justify-center min-h-[50vh]">
        <div className="animate-pulse text-rose-500">{t('elder.loading', 'Loading Elder Care...')}</div>
      </div>
    );
  }

  // Empty State
  if (careProfiles.length === 0) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex flex-col items-center justify-center p-4">
        {/* Main Card */}
        <div className="bg-white dark:bg-slate-900 rounded-[2.5rem] p-10 border border-slate-200 dark:border-slate-800 shadow-sm text-center max-w-md w-full flex flex-col items-center">
          <div className="w-20 h-20 rounded-full bg-rose-100 dark:bg-rose-900/30 flex items-center justify-center mb-6">
            <Heart className="w-10 h-10 text-rose-500 fill-rose-500" />
          </div>
          
          <h1 className="text-3xl font-black text-slate-900 dark:text-white mb-4 tracking-tight">
            {t('elder.title', 'Elder Care')}
          </h1>

          <p className="text-slate-600 dark:text-slate-400 mb-8 font-medium leading-relaxed">
            {t('elder.description', 'Coordinate care for an elder family member. Track medications, vitals, appointments, and connect your care circle.')}
          </p>

          <div className="flex flex-col gap-3 w-full">
            <button 
              onClick={() => setIsCreateModalOpen(true)}
              className="h-11 rounded-2xl bg-rose-500 hover:bg-rose-600 text-white font-black uppercase tracking-widest text-xs flex items-center justify-center gap-2 transition-colors"
            >
              <Plus className="w-4 h-4" />
              {t('elder.create_care_profile', 'Create Care Profile')}
            </button>
            
            <button 
              onClick={() => setIsJoinModalOpen(true)}
              className="h-11 rounded-2xl bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 font-black uppercase tracking-widest text-xs transition-colors"
            >
              {t('elder.join_care_circle', 'Join a Care Circle')}
            </button>
          </div>
        </div>

        {/* Modals for Empty State */}
        {renderCreateModal()}
        {renderJoinModal()}
      </div>
    );
  }

  // List State
  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 p-4 sm:p-6">
      <div className="max-w-page-narrow mx-auto">
        {/* Header */}
        <header className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-black text-slate-900 dark:text-white tracking-tight">{t('elder.title', 'Elder Care')}</h1>
          <button
            onClick={() => setIsCreateModalOpen(true)}
            className="h-11 px-6 rounded-2xl bg-rose-500 hover:bg-rose-600 text-white font-black uppercase tracking-widest text-xs flex items-center gap-2 shadow-lg shadow-rose-500/20 transition-all active:scale-95"
          >
            <Plus className="w-4 h-4" />
            {t('elder.create_profile', 'Create Profile')}
          </button>
        </header>

        {/* Grid */}
        <div className="grid gap-6">
          {careProfiles.map((profile) => (
            <div 
              key={profile.id}
              onClick={() => navigate(`/elder-care/${profile.id}`)}
              className="bg-white dark:bg-slate-900 rounded-[2.5rem] p-6 border border-slate-200 dark:border-slate-800 shadow-sm cursor-pointer hover:shadow-md hover:border-rose-200 dark:hover:border-rose-900/50 transition-all group relative overflow-hidden"
            >
              <div className="flex items-center gap-6">
                {/* Avatar */}
                <div className="relative shrink-0">
                  {profile.photoUrl ? (
                    <img 
                      src={profile.photoUrl} 
                      alt={profile.firstName} 
                      className="w-20 h-20 rounded-full object-cover border-4 border-slate-100 dark:border-slate-800"
                    />
                  ) : (
                    <div className="w-20 h-20 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center border-4 border-slate-100 dark:border-slate-800">
                      <span className="text-2xl font-bold text-slate-400">
                        {getInitials(profile.firstName, profile.lastName)}
                      </span>
                    </div>
                  )}
                  {/* DNR Badge overlay on avatar */}
                  {profile.dnrStatus !== 'none' && (
                    <div className="absolute -bottom-1 -right-1 bg-red-500 text-white text-2xs font-bold px-2 py-0.5 rounded-full border-2 border-white dark:border-slate-900 shadow-sm z-10">
                      DNR
                    </div>
                  )}
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-1">
                    <h2 className="text-xl font-bold text-slate-900 dark:text-white truncate">
                      {profile.firstName} {profile.lastName}
                    </h2>
                    {profile.dnrStatus !== 'none' && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 text-xs font-bold">
                        <AlertCircle className="w-3 h-3" /> DNR
                      </span>
                    )}
                  </div>
                  
                  <p className="text-slate-500 dark:text-slate-400 font-medium mb-3">
                    {calculateAge(profile.dateOfBirth)} years old
                  </p>

                  <div className="flex flex-wrap gap-2">
                    {/* Living Situation Badge */}
                    <span className={`
                      px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wide
                      ${profile.livingSituation === 'co-resident' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-brand' : ''}
                      ${profile.livingSituation === 'independent' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' : ''}
                      ${profile.livingSituation === 'assisted-living' ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300' : ''}
                    `}>
                      {profile.livingSituation.replace('-', ' ')}
                    </span>
                    
                    {/* Check-in Placeholder */}
                    <span className="px-3 py-1 rounded-full bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400 text-xs font-medium">
                      {t('elder.no_checkins', 'No check-ins yet')}
                    </span>
                  </div>
                </div>
                
                {/* Arrow Indicator */}
                <div className="text-slate-300 group-hover:text-rose-500 transition-colors">
                  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Modals for List State */}
      {renderCreateModal()}
      {renderJoinModal()}
    </div>
  );

  function renderCreateModal() {
    if (!isCreateModalOpen) return null;

    return (
      <div 
        className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
        onClick={() => setIsCreateModalOpen(false)}
      >
        <div 
          className="bg-white dark:bg-slate-900 rounded-[2.5rem] p-8 w-full max-w-md shadow-2xl"
          onClick={e => e.stopPropagation()}
        >
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-black text-slate-900 dark:text-white uppercase tracking-widest">{t('elder.modal_create_title', 'New Profile')}</h2>
            <button onClick={() => setIsCreateModalOpen(false)} className="p-2 text-slate-400 hover:text-slate-600">
              <X className="w-6 h-6" />
            </button>
          </div>

          <form onSubmit={handleCreateSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 uppercase mb-1">{t('elder.first_name', 'First Name')}</label>
                <input 
                  type="text" 
                  required
                  className="w-full p-3 rounded-2xl bg-slate-50 dark:bg-slate-800 border-none font-bold text-sm text-slate-700 dark:text-slate-200 focus:ring-2 focus:ring-rose-500 outline-none"
                  value={createForm.firstName}
                  onChange={e => setCreateForm({...createForm, firstName: e.target.value})}
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 uppercase mb-1">{t('elder.last_name', 'Last Name')}</label>
                <input 
                  type="text" 
                  required
                  className="w-full p-3 rounded-2xl bg-slate-50 dark:bg-slate-800 border-none font-bold text-sm text-slate-700 dark:text-slate-200 focus:ring-2 focus:ring-rose-500 outline-none"
                  value={createForm.lastName}
                  onChange={e => setCreateForm({...createForm, lastName: e.target.value})}
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 uppercase mb-1">{t('elder.date_of_birth', 'Date of Birth')}</label>
              <input 
                type="date" 
                required
                className="w-full p-3 rounded-2xl bg-slate-50 dark:bg-slate-800 border-none font-bold text-sm text-slate-700 dark:text-slate-200 focus:ring-2 focus:ring-rose-500 outline-none"
                value={createForm.dateOfBirth}
                onChange={e => setCreateForm({...createForm, dateOfBirth: e.target.value})}
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 uppercase mb-1">{t('elder.living_situation', 'Living Situation')}</label>
              <select 
                className="w-full p-3 rounded-2xl bg-slate-50 dark:bg-slate-800 border-none font-bold text-sm text-slate-700 dark:text-slate-200 focus:ring-2 focus:ring-rose-500 outline-none appearance-none"
                value={createForm.livingSituation}
                onChange={e => setCreateForm({...createForm, livingSituation: e.target.value as LivingSituation})}
              >
                <option value="co-resident">{t('elder.living_co_resident', 'Co-resident')}</option>
                <option value="independent">{t('elder.living_independent', 'Independent')}</option>
                <option value="assisted-living">{t('elder.living_assisted', 'Assisted Living')}</option>
              </select>
            </div>

            <button 
              type="submit"
              className="w-full h-11 mt-4 rounded-2xl bg-rose-500 hover:bg-rose-600 text-white font-black uppercase tracking-widest text-xs"
            >
              {t('elder.create_profile', 'Create Profile')}
            </button>
          </form>
        </div>
      </div>
    );
  }

  function renderJoinModal() {
    if (!isJoinModalOpen) return null;

    return (
      <div 
        className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
        onClick={() => setIsJoinModalOpen(false)}
      >
        <div 
          className="bg-white dark:bg-slate-900 rounded-[2.5rem] p-8 w-full max-w-md shadow-2xl"
          onClick={e => e.stopPropagation()}
        >
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-black text-slate-900 dark:text-white uppercase tracking-widest">{t('elder.modal_join_title', 'Join Circle')}</h2>
            <button onClick={() => setIsJoinModalOpen(false)} className="p-2 text-slate-400 hover:text-slate-600">
              <X className="w-6 h-6" />
            </button>
          </div>

          <form onSubmit={handleJoinSubmit}>
            <div className="mb-6">
              <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 uppercase mb-1">{t('elder.invite_code', 'Invite Code')}</label>
              <input 
                type="text" 
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                required
                placeholder={t('elder.six_digits', '6 digits')}
                className="w-full p-3 rounded-2xl bg-slate-50 dark:bg-slate-800 border-none font-bold text-sm text-slate-700 dark:text-slate-200 focus:ring-2 focus:ring-rose-500 outline-none text-center tracking-[0.5em] text-lg"
                value={joinCode}
                onChange={e => setJoinCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              />
            </div>

            <button 
              type="submit"
              className="w-full h-11 rounded-2xl bg-rose-500 hover:bg-rose-600 text-white font-black uppercase tracking-widest text-xs"
            >
              {t('elder.join_circle', 'Join Circle')}
            </button>
          </form>
        </div>
      </div>
    );
  }
}